import { useAuth, useUser } from '@clerk/nextjs';
import { useCallback, useEffect } from 'react';
import { ClerkPublicMetadata } from '@/code/lib/schemas/linkedinAuth';
import { trpc } from '@/code/lib/utils/trpc';

/**
 * Hook to manage onboarding status and ensure metadata is up-to-date
 * Provides functions to refresh status and check completion
 */
export const useOnboardingStatus = () => {
  const { user, isLoaded: userLoaded } = useUser();
  const { sessionId } = useAuth();
  const utils = trpc.useUtils();

  // Parse the public metadata — available instantly from the session token
  const publicMetadata = user?.publicMetadata as
    | ClerkPublicMetadata
    | undefined;
  const hasCompletedOnboarding =
    publicMetadata?.onboardedLinkedin && publicMetadata?.onboardedCrm;

  // Only query the server if metadata doesn't already confirm completion.
  // This avoids blocking the navbar on a server round-trip for users who
  // have already completed onboarding.
  const setupStatusQuery = trpc.organizations.hasCompletedSetup.useQuery(
    undefined,
    {
      enabled: !!user && userLoaded && !hasCompletedOnboarding,
      // Refetch every 3 seconds if not complete
      refetchInterval: (data) => (!data ? 3000 : false),
      // Keep stale data while refetching
      staleTime: 1000,
    },
  );

  /**
   * Force refresh the user's session and metadata
   */
  const refreshSession = useCallback(async () => {
    try {
      // Reload the Clerk user to get fresh metadata
      await user?.reload();

      // Invalidate the tRPC query to get fresh data
      await utils.organizations.hasCompletedSetup.invalidate();

      // Force a session reload if available
      const clerkInstance = (
        window as Window & {
          Clerk?: { session?: { reload: () => Promise<void> } };
        }
      ).Clerk;
      if (clerkInstance?.session?.reload) {
        await clerkInstance.session.reload();
      }

      return true;
    } catch (_error) {
      // Error handled silently as this is a best-effort refresh
      return false;
    }
  }, [user, utils]);

  /**
   * Check if onboarding is complete and refresh if needed
   */
  const checkAndRefresh = useCallback(async () => {
    // If server says complete but client doesn't reflect it, refresh
    if (setupStatusQuery.data && !hasCompletedOnboarding) {
      await refreshSession();
    }
  }, [setupStatusQuery.data, hasCompletedOnboarding, refreshSession]);

  // Auto-refresh when server status changes
  useEffect(() => {
    if (setupStatusQuery.data && !hasCompletedOnboarding) {
      checkAndRefresh();
    }
  }, [setupStatusQuery.data, hasCompletedOnboarding, checkAndRefresh]);

  // Refresh on session changes
  useEffect(() => {
    if (sessionId && userLoaded) {
      // When session changes (e.g., after OAuth redirect), check status
      utils.organizations.hasCompletedSetup.invalidate();
    }
  }, [sessionId, userLoaded, utils]);

  return {
    isLoading:
      !userLoaded || (!hasCompletedOnboarding && setupStatusQuery.isLoading),
    hasCompletedSetup: setupStatusQuery.data ?? hasCompletedOnboarding ?? false,
    hasCompletedOnboarding: hasCompletedOnboarding ?? false,
    refreshSession,
    checkAndRefresh,
    refetch: setupStatusQuery.refetch,
  };
};

export default useOnboardingStatus;
